import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import useCurrentGlobalSearchVerticalPage from 'hooks/GlobalSearch/useCurrentGlobalSearchVerticalPage'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import useGlobalSearchVerticalTabs from 'hooks/GlobalSearch/useGlobalSearchVerticalTabs'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import Clickable from 'components/Common/Clickable'
import { rem } from 'polished'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import BodyText from 'LuxKit/Typography/BodyText'
import useIsSearchPage from 'hooks/Navigation/useIsSearchPage'
import { matchPath, useLocation } from 'react-router'

const FullSearchInput = styled(Clickable)`
  display: flex;
  border: 1px solid transparent;
  background-color: ${props => props.theme.palette.neutral.default.seven};
  border-radius: ${props => props.theme.borderRadius.S};
  container-type: size;
  align-items: center;
  height: ${rem(40)};
  padding: ${rem(8)} ${rem(16)};
  transition: border-color 0.2s, opacity 0.2s;
  gap: ${rem(12)};
  width: 100%;
  opacity: 1;

  &[hidden] {
    pointer-events: none;
    opacity: 0;
  }

  ${mediaHoverable} {
    &:hover {
      border-color: ${props => props.theme.palette.neutral.default.five};
    }
  }

  &:focus {
    ${interactiveFocusStyle}
  }
`

const InputText = styled(BodyText)`
  @container (width >= 260px) {
    .short {
      display: none;
    }
  }

  @container (width < 260px) {
    .long {
      display: none;
    }
  }
`

interface Props {
  className?: string;
  style?: React.CSSProperties;
  ref?: React.RefObject<HTMLButtonElement | null>;
}

function NavBarSearchInput(props: Props) {
  const { className, style, ref } = props
  const location = useLocation()
  const globalSearchVerticalTabs = useGlobalSearchVerticalTabs()
  const currentSearchVerticalPage = useCurrentGlobalSearchVerticalPage()
  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()

  const isSearchPage = useIsSearchPage()

  const hideSearchBar = useMemo(() => {
    return isSearchPage &&
    // all search pages except the merchandise one provide their own search bars
      !matchPath(location.pathname, { path: '/:regionCode/search/shop' })
  }, [isSearchPage, location.pathname])

  const onSearchClick = useCallback(() => {
    const verticalTab = currentSearchVerticalPage ?? globalSearchVerticalTabs.keys().next().value
    toggleSearchVertical(verticalTab)
  }, [currentSearchVerticalPage, globalSearchVerticalTabs, toggleSearchVertical])

  return <FullSearchInput
    className={className}
    onClick={onSearchClick}
    ref={ref}
    style={style}
    hidden={hideSearchBar}
    aria-hidden={hideSearchBar}
  >
    <LineSearchIcon size="S" />
    <InputText variant="medium" colour="primary-dark" wrap="truncate">
      <span className="long">Search our exclusive travel offers</span>
      <span className="short">Search offers</span>
    </InputText>
  </FullSearchInput>
}

export default React.memo(NavBarSearchInput)
